import { useEffect, useMemo, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  type Edge,
  type Node,
  type NodeProps,
  Handle,
  Position,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import { api, type ScanGraphNode } from "../api";
import { useAppState } from "../AppStateContext";
import { translateMissionStatus, useI18n } from "../i18n";
import "./MissionGraphFlow.css";

type NodeStatus = "pending" | "running" | "completed" | "failed" | "cancelled" | string;

const NODE_W = 200;
const NODE_H = 56;

function statusColor(status: NodeStatus): string {
  switch (status) {
    case "running":
      return "#ffd23f";
    case "completed":
      return "#9be65a";
    case "failed":
      return "#ff5c7a";
    case "cancelled":
      return "#5a5a5a";
    case "pending":
    default:
      return "#7a7a7a";
  }
}

function nodeIcon(type: string): string {
  if (type.startsWith("agent.mission_engine")) return "🎯";
  if (type.startsWith("agent.mission_iteration")) return "🔁";
  if (type.startsWith("agent.mission_thinking")) return "🧠";
  if (type.startsWith("execution.tool.")) return "🛠";
  if (type.startsWith("execution.")) return "⚙";
  if (type.startsWith("compound.")) return "📦";
  if (type.startsWith("flow.")) return "🔀";
  if (type.startsWith("data.")) return "💾";
  if (type.startsWith("llm.")) return "💬";
  if (type.startsWith("knowledge.")) return "📚";
  if (type.startsWith("persistence.")) return "💾";
  return "•";
}

function shortLabel(node: ScanGraphNode): string {
  if (node.label) return node.label;
  const lastDot = node.id.lastIndexOf(".");
  return lastDot >= 0 ? node.id.slice(lastDot + 1) : node.id;
}

function durationStr(ms: number): string {
  if (!ms || ms < 1000) return `${ms || 0}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${(s / 60).toFixed(1)}m`;
}

function flattenNodes(roots: ScanGraphNode[]): ScanGraphNode[] {
  const out: ScanGraphNode[] = [];
  const walk = (n: ScanGraphNode) => {
    out.push(n);
    n.children?.forEach(walk);
  };
  roots.forEach(walk);
  return out;
}

type ApiEdge = { from: string; to: string; kind: string };

function deriveEdgesFromTree(roots: ScanGraphNode[]): ApiEdge[] {
  const out: ApiEdge[] = [];
  const walk = (n: ScanGraphNode) => {
    n.children?.forEach((c) => {
      out.push({ from: n.id, to: c.id, kind: "parent" });
      walk(c);
    });
  };
  roots.forEach(walk);
  return out;
}

type FlowNodeData = {
  raw: ScanGraphNode;
};

type FlowNode = Node<FlowNodeData, "mission">;

function layoutGraph(
  rawNodes: ScanGraphNode[],
  rawEdges: ApiEdge[],
  direction: "LR" | "TB" = "TB",
): { nodes: FlowNode[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: direction, nodesep: 30, ranksep: 70, marginx: 20, marginy: 20 });
  g.setDefaultEdgeLabel(() => ({}));

  rawNodes.forEach((n) => {
    g.setNode(n.id, { width: NODE_W, height: NODE_H });
  });

  // Track which edges we've already added to avoid dagre choking on dupes.
  const seen = new Set<string>();
  rawEdges.forEach((e) => {
    const key = `${e.from}→${e.to}`;
    if (seen.has(key)) return;
    if (!g.hasNode(e.from) || !g.hasNode(e.to)) return;
    seen.add(key);
    g.setEdge(e.from, e.to);
  });

  dagre.layout(g);

  const nodes: FlowNode[] = rawNodes.map((n) => {
    const pos = g.node(n.id);
    return {
      id: n.id,
      type: "mission",
      data: { raw: n },
      position: { x: (pos?.x ?? 0) - NODE_W / 2, y: (pos?.y ?? 0) - NODE_H / 2 },
      sourcePosition: direction === "LR" ? Position.Right : Position.Bottom,
      targetPosition: direction === "LR" ? Position.Left : Position.Top,
    };
  });

  // Colour the flow by where the engine is: the path it has walked glows lime,
  // the edge into whatever is running right now flows (animated dashes), and
  // what's still ahead stays dim.
  const statusOf = new Map(rawNodes.map((n) => [n.id, n.status]));
  const edges: Edge[] = Array.from(seen).map((key) => {
    const [from, to] = key.split("→");
    const src = statusOf.get(from);
    const dst = statusOf.get(to);
    const live = dst === "running";
    const walked = src === "completed" && dst === "completed";
    const stroke = live ? "#ffd23f" : walked ? "rgba(205,255,68,0.7)" : "rgba(255,255,255,0.22)";
    return {
      id: key,
      source: from,
      target: to,
      type: "smoothstep",
      animated: live,
      className: live ? "mgf-edge--live" : walked ? "mgf-edge--walked" : undefined,
      style: { stroke, strokeWidth: live ? 2.2 : walked ? 1.8 : 1.2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: stroke, width: 14, height: 14 },
    };
  });

  return { nodes, edges };
}

function MissionNode({ data }: NodeProps<FlowNode>) {
  const { t } = useI18n();
  const n = data.raw;
  const color = statusColor(n.status);
  const isRunning = n.status === "running";
  return (
    <div
      className={`mgf-node mgf-node--${n.status}${isRunning ? " mgf-node--pulse" : ""}`}
      style={{
        width: NODE_W,
        minHeight: NODE_H,
        borderColor: color,
        boxShadow: isRunning ? `0 0 12px ${color}55` : undefined,
      }}
      title={[
        `id: ${n.id}`,
        `type: ${n.type}`,
        `status: ${n.status}`,
        n.duration_ms ? `duration: ${durationStr(n.duration_ms)}` : "",
        n.iter_total ? `iter: ${n.iter_done}/${n.iter_total}` : "",
        n.event_count ? `events: ${n.event_count}` : "",
        n.error_message ? `error: ${n.error_message}` : "",
      ]
        .filter(Boolean)
        .join("\n")}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} isConnectable={false} />
      <div className="mgf-node__row">
        <span className="mgf-node__icon">{nodeIcon(n.type)}</span>
        <span className="mgf-node__label">{shortLabel(n)}</span>
      </div>
      <div className="mgf-node__meta">
        <span className="mgf-node__status" style={{ color }}>
          {translateMissionStatus(n.status, t)}
        </span>
        {n.duration_ms ? <span className="mgf-node__dur">{durationStr(n.duration_ms)}</span> : null}
        {n.iter_total > 0 ? (
          <span className="mgf-node__iter">
            {n.iter_done}/{n.iter_total}
          </span>
        ) : null}
        {n.event_count > 0 ? <span className="mgf-node__events">{n.event_count}ev</span> : null}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} isConnectable={false} />
    </div>
  );
}

const NODE_TYPES = { mission: MissionNode };

export function MissionGraphFlow({
  scanId,
  height = 560,
  fill = false,
}: {
  scanId: string | null;
  height?: number | string;
  /** Stretch to fill the parent's height (flex) instead of a fixed `height`.
   *  Used on the dedicated Graph page so the canvas fills the viewport. */
  fill?: boolean;
}) {
  const { t } = useI18n();
  const { activeScanGraph } = useAppState();
  const [fetchedGraph, setFetchedGraph] = useState<{
    nodes: ScanGraphNode[];
    edges?: ApiEdge[];
    pipeline_name?: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [direction, setDirection] = useState<"LR" | "TB">("TB");

  // AppState already polls /scans/{id}/graph for the displayed scan every 5s.
  // When this view shows that same scan, reuse the shared graph instead of
  // opening a second poller on the identical endpoint — otherwise the graph is
  // fetched twice every 5s, doubling rate-limit pressure on /graph.
  const sharedGraph =
    scanId && activeScanGraph && activeScanGraph.scan_id === scanId ? activeScanGraph : null;
  const selfPoll = !!scanId && !sharedGraph;

  // Self-poll only for a scan AppState isn't already tracking (e.g. an older or
  // pinned scan). setState lives in the async callbacks; the rendered graph is
  // derived below rather than mirrored into state inside the effect.
  useEffect(() => {
    if (!selfPoll || !scanId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const load = () => {
      api
        .scanGraph(scanId)
        .then((res) => {
          if (cancelled) return;
          setFetchedGraph({
            nodes: res.nodes || [],
            edges: res.edges,
            pipeline_name: res.pipeline_name,
          });
          setError(null);
        })
        .catch((e) => {
          if (cancelled) return;
          setError(String(e?.message || e));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    load();
    const tid = window.setInterval(load, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(tid);
    };
  }, [selfPoll, scanId]);

  // Render source: the shared graph when AppState polls this scan, else the
  // self-polled one. Neither (no scan, or a not-yet-fetched other scan) → empty.
  const source = sharedGraph ?? (selfPoll ? fetchedGraph : null);
  const rawNodes = useMemo(() => flattenNodes(source?.nodes ?? []), [source]);
  const rawEdges = useMemo<ApiEdge[]>(
    () =>
      source && source.edges && source.edges.length > 0
        ? source.edges
        : deriveEdgesFromTree(source?.nodes ?? []),
    [source],
  );
  const pipelineName = source?.pipeline_name ?? "";

  const { flowNodes, flowEdges } = useMemo(() => {
    if (rawNodes.length === 0) return { flowNodes: [] as FlowNode[], flowEdges: [] as Edge[] };
    const { nodes, edges } = layoutGraph(rawNodes, rawEdges, direction);
    return { flowNodes: nodes, flowEdges: edges };
  }, [rawNodes, rawEdges, direction]);

  const stats = useMemo(() => {
    const acc: Record<string, number> = {};
    rawNodes.forEach((n) => {
      acc[n.status] = (acc[n.status] || 0) + 1;
    });
    return acc;
  }, [rawNodes]);

  if (!scanId) {
    return (
      <section className={`mgf${fill ? " mgf--fill" : ""}`}>
        <div className="mgf__header">
          <h2 className="mgf__title">{t("scan.graphTitle")}</h2>
        </div>
        <div className="mgf__empty">{t("scan.graphEmpty")}</div>
      </section>
    );
  }

  return (
    <section className={`mgf${fill ? " mgf--fill" : ""}`} data-tour="overview-graph">
      <div className="mgf__header">
        <h2 className="mgf__title">
          {t("scan.graphTitle")}{" "}
          <span className="mgf__pipeline">· {pipelineName || "?"}</span>
        </h2>
        <div className="mgf__header-right">
          <div className="mgf__stats">
            {Object.entries(stats).map(([s, n]) => (
              <span key={s} className="mgf__stat" style={{ color: statusColor(s) }}>
                {translateMissionStatus(s, t)}: {n}
              </span>
            ))}
          </div>
          <div className="mgf__dir">
            <button
              type="button"
              className={`mgf__dir-btn${direction === "TB" ? " mgf__dir-btn--active" : ""}`}
              onClick={() => setDirection("TB")}
              title={t("scan.graphDirTB")}
            >
              ↓
            </button>
            <button
              type="button"
              className={`mgf__dir-btn${direction === "LR" ? " mgf__dir-btn--active" : ""}`}
              onClick={() => setDirection("LR")}
              title={t("scan.graphDirLR")}
            >
              →
            </button>
          </div>
        </div>
      </div>
      {error ? <p className="mgf__error">{t("scan.graphLoadFailed", { error })}</p> : null}
      <div className="mgf__canvas" style={{ height: fill ? undefined : height }}>
        {loading && flowNodes.length === 0 ? (
          <div className="mgf__loading">{t("scan.graphLoading")}</div>
        ) : flowNodes.length === 0 && !error ? (
          <div className="mgf__loading">{t("scan.graphNoNodes")}</div>
        ) : (
          <ReactFlow
            nodes={flowNodes}
            edges={flowEdges}
            nodeTypes={NODE_TYPES}
            fitView
            minZoom={0.15}
            maxZoom={2.5}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable
            proOptions={{ hideAttribution: true }}
            colorMode="dark"
          >
            <Background gap={18} size={1} color="rgba(255,255,255,0.05)" />
            <Controls showInteractive={false} />
            <MiniMap
              nodeColor={(node) => {
                const status = (node.data as FlowNodeData | undefined)?.raw.status ?? "pending";
                return statusColor(status);
              }}
              maskColor="rgba(0,0,0,0.6)"
              pannable
              zoomable
            />
          </ReactFlow>
        )}
      </div>
    </section>
  );
}
